import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { GdpAccess, GdpAccessEntry, GdpAccessType, GdpAccount } from '../models/gdp-access.model';

/**
 * Cachea GET /gdp/me para condicionar la pestaña "Gestión" y las secciones de
 * admin, con persistencia en localStorage (stale-while-revalidate): al arrancar
 * se usa el último valor conocido (la pestaña y el guard responden al instante)
 * y la respuesta fresca lo corrige en segundo plano si el acceso cambió. Es
 * solo gating de UI: los permisos reales los impone el backend en cada request.
 */
@Injectable({ providedIn: 'root' })
export class GdpAccessService {
  private static readonly STORAGE_KEY = 'gdp.access';

  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.baseApiUrl}/gdp`;

  private readonly state = signal<GdpAccess | null>(this.readStored());
  private inFlight$?: Observable<GdpAccess>;

  readonly hasGdpAccess = computed(() => this.state()?.hasAccess === true);
  readonly isAdmin = computed(() => this.hasGdpAccess() && this.state()?.accessType === 'ADMIN');

  /** Dispara /gdp/me revalidando en segundo plano (la UI ya ve el cacheado). */
  load(): void {
    this.refresh().subscribe();
  }

  /** Para el guard: valor cacheado (memoria/localStorage) o espera la petición. */
  ensureLoaded(): Observable<GdpAccess> {
    const cached = this.state();
    if (cached) {
      return of(cached);
    }
    return this.refresh();
  }

  /** Pide /gdp/me y persiste el resultado. Ante error de red/servidor conserva
   *  el último valor conocido (no esconde la pestaña por un backend caído). */
  private refresh(): Observable<GdpAccess> {
    if (!this.inFlight$) {
      this.inFlight$ = this.http.get<GdpAccess>(`${this.apiUrl}/me`).pipe(
        catchError(() => of(this.state() ?? ({ hasAccess: false, accessType: null } as GdpAccess))),
        tap(access => this.store(access)),
        finalize(() => (this.inFlight$ = undefined)),
        shareReplay(1)
      );
    }
    return this.inFlight$;
  }

  private store(access: GdpAccess): void {
    this.state.set(access);
    try {
      localStorage.setItem(GdpAccessService.STORAGE_KEY, JSON.stringify(access));
    } catch { /* almacenamiento lleno/bloqueado: seguimos solo con memoria */ }
  }

  private readStored(): GdpAccess | null {
    try {
      const raw = localStorage.getItem(GdpAccessService.STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as GdpAccess;
      return typeof parsed?.hasAccess === 'boolean' ? parsed : null;
    } catch {
      return null;
    }
  }

  /** Al hacer logout: el cacheado es de la cuenta saliente. */
  reset(): void {
    this.state.set(null);
    this.inFlight$ = undefined;
    try {
      localStorage.removeItem(GdpAccessService.STORAGE_KEY);
    } catch { /* sin acceso a storage */ }
  }

  // ── Cuentas ────────────────────────────────────────────────────────────────

  /** Cuentas con acceso GDP activo: candidatas a participante (cualquier usuario GDP). */
  getSelectableAccounts(): Observable<GdpAccount[]> {
    return this.http.get<GdpAccount[]>(`${this.apiUrl}/accounts/selectable`);
  }

  /** Candidatos a participante de un sprint: participantes del proyecto sin observadores. */
  getSprintSelectableAccounts(sprintId: number): Observable<GdpAccount[]> {
    return this.http.get<GdpAccount[]>(`${this.apiUrl}/sprints/${sprintId}/selectable-accounts`);
  }

  // ── Gestión de accesos (solo ADMIN) ────────────────────────────────────────

  getAccounts(): Observable<GdpAccount[]> {
    return this.http.get<GdpAccount[]>(`${this.apiUrl}/accounts`);
  }

  grantAccess(accountId: number, accessType: GdpAccessType): Observable<GdpAccessEntry> {
    return this.http.post<GdpAccessEntry>(`${this.apiUrl}/access`, { accountId, accessType });
  }

  updateAccess(accessId: number, accessType: GdpAccessType, active: boolean): Observable<GdpAccessEntry> {
    return this.http.put<GdpAccessEntry>(`${this.apiUrl}/access/${accessId}`, { accessType, active });
  }

  revokeAccess(accessId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/access/${accessId}`);
  }
}
